import { z } from 'zod'

/**
 * The view state the three rules pages read from the URL.
 *
 * Beside the pages rather than in the route files, so imports point one way
 * and the pruning below is covered. Every field is optional with a `.catch`,
 * because a query string is user input; defaults are applied at read time and
 * stripped on the way out, so `/marketing/rules-v2` stays the clean URL of the
 * default view. See CLAUDE.md §4.5 for the longer argument.
 */

const page = z.number().int().positive().optional().catch(undefined)

// --- /marketing/rules-v2 -----------------------------------------------------

// Exclusions were a tab here; they are a page of their own now, under
// Exceptions. An old `?tab=exclusions` link falls back to the Rules tab.
export const RULES_TABS = ['rules', 'automation', 'config'] as const
export type RulesTab = (typeof RULES_TABS)[number]

/** The decisions feed's modes: a config's LIVE or SHADOW, or the ±band. */
export const DECISION_MODES = ['LIVE', 'SHADOW', 'BAND'] as const
export type DecisionMode = (typeof DECISION_MODES)[number]

export const rulesSearchSchema = z.object({
  tab: z
    .enum(RULES_TABS)
    .optional()
    .catch(undefined)
    .describe('The tab shown: rules, automation or config; default rules.'),
  /** The rule whose details are open on the Rules tab. */
  rule: z
    .string()
    .optional()
    .catch(undefined)
    .describe('Code of the rule whose details are open on the Rules tab.'),
  /** The page of the Automation list. */
  page: page.describe('Page of the Automation list, 1-based; default 1.'),
  /** The Automation tab's mode filter. */
  mode: z
    .enum(['LIVE', 'SHADOW', 'OFF'])
    .optional()
    .catch(undefined)
    .describe('Automation list mode filter (LIVE, SHADOW or OFF); absent means all modes.'),
  /**
   * The Rules tab's store: runs for it are validation-only dry runs. Absent
   * runs across every store.
   */
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe(
      'Store the Rules tab runs against, by id; runs for it are forced dry runs. Absent runs across every store.',
    ),
  /** The Automation tab's decisions feed: its page, its modes and its search. */
  dpage: page.describe('Page of the automated decisions feed, 1-based; default 1.'),
  dmodes: z
    .array(z.enum(DECISION_MODES))
    .optional()
    .catch(undefined)
    .describe('Decision modes the feed shows (LIVE, SHADOW, BAND); absent means all.'),
  dq: z.string().optional().catch(undefined).describe('Search over the automated decisions feed.'),
})

export type RulesSearch = z.infer<typeof rulesSearchSchema>

export function withoutRulesSearchDefaults(search: RulesSearch): RulesSearch {
  const pruned: RulesSearch = {}
  if (search.tab !== undefined && search.tab !== 'rules') pruned.tab = search.tab
  if (search.rule) pruned.rule = search.rule
  if (search.page !== undefined && search.page !== 1) pruned.page = search.page
  if (search.mode !== undefined) pruned.mode = search.mode
  if (search.store !== undefined) pruned.store = search.store
  if (search.dpage !== undefined && search.dpage !== 1) pruned.dpage = search.dpage
  if (search.dmodes && search.dmodes.length > 0) pruned.dmodes = search.dmodes
  if (search.dq) pruned.dq = search.dq
  return pruned
}

// --- /marketing/rules-v2/exclusions -------------------------------------------

export const exclusionsSearchSchema = z.object({
  page: page.describe('Page of the exclusion list, 1-based; default 1.'),
})

export type ExclusionsSearch = z.infer<typeof exclusionsSearchSchema>

export function withoutExclusionsSearchDefaults(search: ExclusionsSearch): ExclusionsSearch {
  const pruned: ExclusionsSearch = {}
  if (search.page !== undefined && search.page !== 1) pruned.page = search.page
  return pruned
}

// --- /marketing/rules-v2/executions -------------------------------------------

export const executionsSearchSchema = z.object({
  page: page.describe('Page of the run list, 1-based; default 1.'),
  /** Only dry runs, or only runs that could act. Absent shows both. */
  kind: z
    .enum(['dry', 'live'])
    .optional()
    .catch(undefined)
    .describe('Only dry runs or only live runs; absent shows both.'),
})

export type ExecutionsSearch = z.infer<typeof executionsSearchSchema>

export function withoutExecutionsSearchDefaults(search: ExecutionsSearch): ExecutionsSearch {
  const pruned: ExecutionsSearch = {}
  if (search.page !== undefined && search.page !== 1) pruned.page = search.page
  if (search.kind !== undefined) pruned.kind = search.kind
  return pruned
}

// --- /marketing/rules-v2/executions/$executionId ------------------------------

export const executionSearchSchema = z.object({
  /** Free-text search over the run's rows. */
  q: z
    .string()
    .optional()
    .catch(undefined)
    .describe("Free-text search over the run's matched rows."),
  /**
   * Rule codes to show rows for. The router parses search values as JSON, so
   * `?rules=["TR1"]` arrives as an array; anything else is dropped.
   */
  rules: z
    .array(z.string())
    .optional()
    .catch(undefined)
    .describe('Rule codes the rows are narrowed to; absent means every rule.'),
  /** The row whose detail panel is open. */
  row: z
    .string()
    .optional()
    .catch(undefined)
    .describe('Key of the row whose detail sheet is open.'),
  /** The page of the filtered rows. */
  page: page.describe('Page of the filtered rows, 1-based; default 1.'),
})

export type ExecutionSearch = z.infer<typeof executionSearchSchema>

export function withoutExecutionSearchDefaults(search: ExecutionSearch): ExecutionSearch {
  const pruned: ExecutionSearch = {}
  if (search.q) pruned.q = search.q
  if (search.rules && search.rules.length > 0) pruned.rules = search.rules
  if (search.row) pruned.row = search.row
  if (search.page !== undefined && search.page !== 1) pruned.page = search.page
  return pruned
}
